<script>
import { SAVE_BAR_Z_INDEX } from "@/utilities/overlay-layers";

export default {
  name: "SaveBar",
  props: {
    inProgress: { type: Boolean, required: true },
    // Lit and clickable: there is something to save or restore. The page
    // decides what that is, usually its unsaved changes.
    active: { type: Boolean, required: true },
    showCancel: { type: Boolean, default: false },
    showRestore: { type: Boolean, default: false },
    anchorEl: {
      type: [Object, HTMLElement],
      default: null,
    },
    scrollRoot: {
      type: [Object, HTMLElement],
      default: null,
    },
  },
  data() {
    return {
      anchorBox: { left: 0, width: 0 },
      ro: null,
      zIndex: SAVE_BAR_Z_INDEX,
    };
  },
  computed: {
    primaryColor() {
      const theme = this.$vuetify.theme.themes;
      return this.$vuetify.theme.dark
        ? theme.dark.primary
        : theme.light.primary;
    },
  },
  methods: {
    submitChanges() {
      this.$emit("submit");
    },
    cancelChanges() {
      this.$emit("cancel");
    },
    updateAnchorBox() {
      const el = this.anchorEl instanceof Element ? this.anchorEl : null;
      if (!el) {
        this.anchorBox = { left: 0, width: window.innerWidth };
        return;
      }
      const rect = el.getBoundingClientRect();
      this.anchorBox = {
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      };
    },
    addListeners() {
      this._onResizeOrScroll = () => this.updateAnchorBox();
      window.addEventListener("resize", this._onResizeOrScroll, {
        passive: true,
      });
      window.addEventListener("scroll", this._onResizeOrScroll, {
        passive: true,
      });
      window.addEventListener("orientationchange", this._onResizeOrScroll);
      const scrollEl = this.getScrollElement();
      if (scrollEl && scrollEl !== window) {
        scrollEl.addEventListener("scroll", this._onResizeOrScroll, {
          passive: true,
        });
      }
      if (this.anchorEl instanceof Element && "ResizeObserver" in window) {
        this.ro = new ResizeObserver(() => this.updateAnchorBox());
        this.ro.observe(this.anchorEl);
      }
    },
    removeListeners() {
      window.removeEventListener("resize", this._onResizeOrScroll);
      window.removeEventListener("scroll", this._onResizeOrScroll);
      window.removeEventListener("orientationchange", this._onResizeOrScroll);
      const scrollEl = this.getScrollElement();
      if (scrollEl && scrollEl !== window) {
        scrollEl.removeEventListener("scroll", this._onResizeOrScroll);
      }
      if (this.ro) {
        this.ro.disconnect();
        this.ro = null;
      }
    },
    getScrollElement() {
      if (this.scrollRoot instanceof Element) {
        return this.scrollRoot;
      }
      return window;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateAnchorBox();
      this.addListeners();
    });
  },
  beforeDestroy() {
    this.removeListeners();
  },
  watch: {
    anchorEl: {
      handler() {
        this.$nextTick(() => {
          this.removeListeners();
          this.updateAnchorBox();
          this.addListeners();
        });
      },
      immediate: false,
    },
    scrollRoot: {
      handler() {
        this.$nextTick(() => {
          this.removeListeners();
          this.updateAnchorBox();
          this.addListeners();
        });
      },
      immediate: false,
    },
  },
};
</script>

<template>
  <div
    class="save-bar-wrapper"
    :style="{
      left: anchorBox.left + 'px',
      width: anchorBox.width + 'px',
      zIndex: zIndex,
    }"
  >
    <div
      class="save-bar"
      :style="{
        boxShadow: !active
          ? '0 0 10px rgba(0, 0, 0, 0.2)'
          : `0 0 15px ${primaryColor}66`,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      }"
    >
      <div class="d-flex align-center">
        <v-icon color="primary" class="save-bar-icon mr-2"
          >mdi-content-save</v-icon
        >
        <span class="save-bar-label mr-4">{{
          $t("actions.save-changes")
        }}</span>
        <v-spacer />
        <v-btn
          v-if="showCancel"
          text
          class="save-bar-btn"
          @click="cancelChanges"
        >
          {{ $t("actions.cancel") }}
        </v-btn>
        <v-btn
          v-if="showRestore"
          :disabled="inProgress || !active"
          text
          class="save-bar-btn"
          @click="cancelChanges"
        >
          {{ $t("actions.restore") }}
        </v-btn>
        <v-btn
          color="primary"
          class="save-bar-btn save-bar-btn--primary"
          :loading="inProgress"
          :disabled="inProgress || !active"
          @click="submitChanges"
        >
          {{ $t("actions.save") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
:root {
  --save-bar-height: 56px;
  --save-bar-gap: 12px;
}

.save-bar-wrapper {
  position: fixed;
  bottom: 12px;
  pointer-events: none;
  padding-bottom: calc(var(--save-bar-gap) + env(safe-area-inset-bottom));
}

.save-bar {
  pointer-events: auto;
  min-height: var(--save-bar-height);
  border-radius: 12px;
  padding: 8px 12px;
  backdrop-filter: blur(10px);
}

.save-bar-btn {
  margin: 0 0 0 8px !important;
  min-width: 0 !important;
  height: 36px !important;
  padding: 0 16px !important;
  letter-spacing: normal;
  text-transform: none;
}

.save-bar-btn--primary {
  padding: 0 20px !important;
}

/* On a phone the bar drops its icon and label and tightens the buttons, so
   both stay inside the bar. Last in the file: the paddings above carry
   !important too, and Vuetify's own icon and size rules tie on specificity. */
@media (max-width: 599px) {
  .save-bar-label,
  .save-bar-icon {
    display: none !important;
  }

  .save-bar-btn,
  .save-bar-btn--primary {
    padding: 0 8px !important;
    font-size: 13px !important;
  }
}
</style>
